// RUT chileno (Rol Único Tributario) como funciones puras, sin zod.
//
// El dígito verificador es el módulo 11 estándar: se pondera el cuerpo de
// derecha a izquierda con 2,3,4,5,6,7,2,3,…, se suma, y el verificador es
// 11 − (suma mod 11), donde 11 → «0» y 10 → «K». `rutSchema` (./zod.ts)
// aplica el mismo cálculo para el cable; este módulo es para quien
// necesita validar, normalizar o mostrar un RUT fuera de un schema:
// la ruta PATCH /organizations/me (400 RUT_INVALID antes de guardar) y
// cualquier cliente que quiera formatear sin arrastrar zod (ver la nota
// del barrel en ./index.ts y el subpath "@bitacorapro/shared/rut").
//
// Tres formas:
//   - canónica (la que se guarda):  «12345678-5»
//   - para mostrar:                 «12.345.678-5»
//   - lo que la gente escribe:      con o sin puntos (bien agrupados),
//                                   SIEMPRE con guión antes del verificador,
//                                   k minúscula, espacios alrededor, ceros
//                                   a la izquierda.
//
// El guión es obligatorio a propósito: sin él, un cuerpo de 8 dígitos
// escrito sin verificador («76000018») se leería como el RUT de 7 dígitos
// «7600001-8», que cuadra en módulo 11 una de cada once veces, y se
// guardaría un RUT distinto al de la empresa sin que nadie lo note. Es la
// misma forma que acepta `rutSchema` (RUT_RAW en ./zod.ts).
/** Dígito verificador módulo 11 del cuerpo numérico (sin puntos ni guión). */
export function computeRutCheckDigit(body) {
    let sum = 0;
    let factor = 2;
    for (let i = body.length - 1; i >= 0; i--) {
        const digit = body.charCodeAt(i) - 48;
        sum += digit * factor;
        factor = factor === 7 ? 2 : factor + 1;
    }
    const remainder = 11 - (sum % 11);
    if (remainder === 11)
        return "0";
    if (remainder === 10)
        return "K";
    return String(remainder);
}
// Cuerpo de 1 a 8 dígitos — con puntos de miles bien puestos o sin
// ninguno — guión y verificador, una vez recortados los espacios. Un
// cuerpo de 9+ dígitos no es un RUT (el padrón no llega). Misma forma que
// RUT_RAW en ./zod.ts.
const RUT_INPUT = /^(\d{1,3}(?:\.\d{3}){0,2}|\d{1,8})-([0-9kK])$/;
/**
 * «12.345.678-5», «12345678-5», « 12345678-5 », «8765432-k» u «07.654.321-6»
 * → la forma canónica «12345678-5» (sin puntos ni ceros a la izquierda,
 * guión, K mayúscula). null cuando no tiene forma de RUT, le falta el
 * guión o el verificador no cuadra.
 */
export function normalizeRut(input) {
    const match = RUT_INPUT.exec(input.trim());
    if (!match)
        return null;
    // Los ceros a la izquierda no cambian el verificador (pesan 0) pero sí
    // la forma guardada: «07654321-6» y «7654321-6» son el mismo RUT.
    const body = match[1].replaceAll(".", "").replace(/^0+/, "");
    // Un cuerpo todo ceros no es nadie.
    if (body === "")
        return null;
    const provided = match[2].toUpperCase();
    return computeRutCheckDigit(body) === provided ? `${body}-${provided}` : null;
}
/** true si `input` es un RUT con verificador correcto (misma tolerancia que normalizeRut). */
export function isValidRut(input) {
    return normalizeRut(input) !== null;
}
/**
 * Para mostrar: «12.345.678-5». Acepta cualquier forma que normalizeRut
 * acepte; si no es un RUT válido devuelve el texto tal cual (recortado),
 * así un campo con basura no se «arregla» en silencio.
 */
export function formatRut(input) {
    const normalized = normalizeRut(input);
    if (!normalized)
        return input.trim();
    const [body, dv] = normalized.split("-");
    const withDots = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${withDots}-${dv}`;
}
//# sourceMappingURL=rut.js.map